import { FC, useCallback, useState } from 'react';
import { Field } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { Button } from '@mui/material';

import FormCheckbox from '@components/FormCheckbox/FormCheckbox';
import FormSelect from '@components/FormSelect/FormSelect';
import FormTextField from '@components/FormTextField/FormTextField';
import useUnsavedChanges from '@hooks/useUnsavedChanges';
import useProgramCodesActive from '@store/hooks/useProgramCodesActive';

import { IOrganizationDetailsFormProps } from './OrganizationDetailsForm.interface';

import './style.scss';

const OrganizationDetailsForm: FC<IOrganizationDetailsFormProps> = ({
  form,
  handleSubmit,
  hasValidationErrors,
  submitting,
  values,
  isNew,
  isActive,
  isActivatingOrDeactivating,
  onActivateOrDeactivate,
  onCancel,
}) => {
  const { t } = useTranslation();
  const programCodes = useProgramCodesActive();

  const [accessKeyEditable, setAccessKeyEditable] = useState(isNew);

  const handleEditAccessKey = useCallback(() => {
    setAccessKeyEditable(true);
    form.change('accessCode', '');

    setTimeout(() => {
      const input = document.querySelector('input[name="accessCode"]') as HTMLInputElement;
      input?.focus();
    });
  }, [form]);

  useUnsavedChanges();

  return (
    <div className="organization-details-form">
      <div className="organization-details-form__fieldset">
        <Field
          component={FormSelect}
          label={t('admin.programCode')}
          name="programCodeId"
          options={programCodes}
          disabled={!isNew}
          required
        />

        <Field component={FormTextField} label={t('admin.name')} name="name" required />
      </div>

      <div className="organization-details-form__fieldset">
        <Field
          component={FormTextField}
          className="organization-details-form__access-key"
          label={t('admin.accessKey')}
          name="accessCode"
          disabled={!accessKeyEditable}
          type={accessKeyEditable ? 'text' : 'password'}
          required
        />

        {!accessKeyEditable && (
          <Button
            variant="text"
            className="organization-details-form__change-access-key"
            onClick={handleEditAccessKey}
          >
            {t('admin.changeAccessKey')}
          </Button>
        )}
      </div>

      <div className="organization-details-form__fieldset organization-details-form__fieldset_checkboxes">
        <Field
          component={FormCheckbox}
          name="usesSports"
          label={t('admin.usesSports')}
          type="checkbox"
        />

        <Field
          component={FormCheckbox}
          name="usesAthleteRoles"
          label={t('admin.usesAthleteRoles')}
          type="checkbox"
        />

        <Field
          component={FormCheckbox}
          name="usesGender"
          label={t('admin.usesGender')}
          type="checkbox"
        />

        <Field
          component={FormCheckbox}
          name="showRiskList"
          label={t('admin.showRiskList')}
          type="checkbox"
        />

        <Field
          component={FormCheckbox}
          name="showMedicationExceptionLink"
          label={t('admin.showMedicationExceptionLink')}
          type="checkbox"
        />
      </div>

      <div className="organization-details-form__fieldset">
        <Field
          component={FormTextField}
          label={t('admin.medicationExceptionFileName')}
          name="medicationExceptionFileName"
          disabled={!values.showMedicationExceptionLink}
          required
        />
      </div>

      <div className="organization-details-form__fieldset">
        <Field
          component={FormTextField}
          label={t('admin.medicationExceptionFileURL')}
          name="medicationExceptionFileUrl"
          disabled={!values.showMedicationExceptionLink}
          required
        />
      </div>

      <div className="organization-details-form__controls">
        <Button variant="outlined" onClick={onCancel}>
          {t('admin.cancel')}
        </Button>

        {!isNew && (
          <Button
            variant="outlined"
            disabled={isActivatingOrDeactivating}
            onClick={onActivateOrDeactivate}
          >
            {isActive ? t('admin.deactivate') : t('admin.activate')}
          </Button>
        )}

        <Button
          variant="contained"
          disabled={hasValidationErrors || submitting}
          onClick={handleSubmit}
        >
          {isNew ? t('admin.create') : t('admin.save')}
        </Button>
      </div>
    </div>
  );
};

export default OrganizationDetailsForm;
